<template>
    <page-layout>
        <template #header>
            <page-title title="Deliverables" @on-close="handleClose" />
        </template>
        <g-loader v-if="isLoading" type="fixed" />
        <div v-else>
            <g-button icon="angle-left" class="margin-bottom-large" size="small" @click="handleBack">Back</g-button>

            <g-typography tag="h4" font-weight="semibold" :value="task.name" class="margin-bottom-small" />

            <div class="margin-bottom-small">
                <activation-task-icon :task="task" />
                <g-typography tag="span" size="small" :value="getLabel(task)" />
            </div>

            <g-typography tag="p" class="margin-bottom-large">
                You must submit content for approval for each deliverable before you post. The task will be finished
                once you post {{ deliverables.length > 1 ? 'all deliverables' : 'your deliverable' }}.
            </g-typography>

            <g-typography tag="h4" font-weight="semibold" value="Your Deliverables" class="margin-bottom-small" />

            <g-base-box class="g-mt-5" padding="none">
                <div v-for="(deliverable, i) in deliverables" :key="deliverable.id">
                    <clickable-list-item
                        :image="deliverableImage(deliverable)"
                        :icon="deliverableFileIcon(deliverable)"
                        :is-last="i === deliverables.length - 1"
                        @click="handleOpenDeliverable(deliverable)"
                    >
                        <template #middle>
                            <div class="g-mb-3">Deliverable {{ deliverable.index }}</div>
                            <div v-if="!deliverable.is_complete">
                                <g-tag :theme="CONTENT_APPROVAL_STATUS_COLORS[deliverable.approval_status]">
                                    {{
                                        deliverable.approval_status
                                            ? CONTENT_APPROVAL_STATUS_LABELS[deliverable.approval_status]
                                            : CONTENT_APPROVAL_STATUS_LABELS[CONTENT_APPROVAL_STATUS_NONE]
                                    }}
                                </g-tag>
                            </div>
                            <div v-else>
                                <g-icon name="check-circle" type="solid" size="small" color="green-5" /> Completed
                            </div>
                        </template>
                    </clickable-list-item>
                </div>
            </g-base-box>
        </div>
    </page-layout>
</template>

<script>
    import { computed } from 'vue-demi';
    import { PLACEHOLDER_IMAGE } from '../../../constants/cdn-assets';
    import { IMAGE_MIME_TYPES } from '../../../constants/file-mime-types';
    import FILE_ICON_NAMES from '../../../constants/file-icon-names';
    import {
        CONTENT_APPROVAL_STATUS_COLORS,
        CONTENT_APPROVAL_STATUS_LABELS,
        CONTENT_APPROVAL_STATUS_NONE,
        CONTENT_APPROVAL_FILE_TYPE
    } from '../../../constants/content-approval';
    import PageLayout from '../../layouts/PageLayout.vue';
    import PageTitle from '../../library/PageTitle.vue';
    import ClickableListItem from '../../library/ClickableListItem.vue';
    import ActivationTaskIcon from './partials/tasks/ActivationTaskIcon.vue';
    import { getMediaLibraryFilePaths } from '../../../helpers/activations/activation-files';
    import ActivationTasksMixin from '../../data-views/activation-briefs/mixins/ActivationTasksMixin';
    import useTaskDelivery from './composables/use-task-delivery';
    import useEventTracking from '../../../../src/composables/use-event-tracking';
    import { EVENT_NAME_CONTENT_APPROVAL_VIEW_DELIVERABLE } from '../../../constants/analytics-events';
    import useRoute from '../../../../src/composables/use-route';

    export default {
        name: 'ActivationDeliverablesView',

        components: {
            PageLayout,
            PageTitle,
            ClickableListItem,
            ActivationTaskIcon
        },
        mixins: [ActivationTasksMixin],
        props: {
            brief: {
                type: Object,
                default: null
            },
            task: {
                type: Object,
                default: null
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            deliverables: {
                type: Array,
                default: null
            }
        },
        setup(props, { emit }) {
            const events = useEventTracking();
            const route = useRoute().get();

            const { goToDeliverablePage } = useTaskDelivery();

            const isImage = (file) => {
                return IMAGE_MIME_TYPES.includes(file?.mime_type);
            };

            const deliverableFile = (deliverable) => {
                const files = getMediaLibraryFilePaths(
                    deliverable?.last_approval_content_activity?.content?.files,
                    CONTENT_APPROVAL_FILE_TYPE
                );

                return files?.length ? files[0] : null;
            };

            const deliverableImage = (deliverable) => {
                const file = deliverableFile(deliverable);
                if (!file) {
                    return PLACEHOLDER_IMAGE;
                }

                return file && isImage(file) ? file.url : null;
            };

            const deliverableFileIcon = (deliverable) => {
                const file = deliverableFile(deliverable);
                if (!file) {
                    return null;
                }

                if (FILE_ICON_NAMES.hasOwnProperty(file.mime_type)) {
                    return FILE_ICON_NAMES[file.mime_type];
                }

                return 'file';
            };

            const handleClose = () => {
                emit('close');
            };

            const handleBack = () => {
                emit('back');
            };

            const handleOpenDeliverable = (deliverable) => {
                events.trackEvent(
                    EVENT_NAME_CONTENT_APPROVAL_VIEW_DELIVERABLE,
                    getViewDeliverableClickedEventPayload.value,
                    false
                );
                goToDeliverablePage(deliverable, {
                    context: route.params.context,
                    back_route: route.params.back_route
                });
            };

            const getViewDeliverableClickedEventPayload = computed(() => {
                return {
                    activation_id: props.brief.activation.id,
                    contact_id: props.brief.contact_id
                };
            });

            return {
                CONTENT_APPROVAL_STATUS_COLORS,
                CONTENT_APPROVAL_STATUS_LABELS,
                CONTENT_APPROVAL_STATUS_NONE,
                PLACEHOLDER_IMAGE,
                deliverableImage,
                deliverableFileIcon,
                handleClose,
                handleBack,
                handleOpenDeliverable
            };
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }
</style>
